import UserControllerBase from '../base'
import type {
  NGetOrganizationsByUserId,
  NGetSession,
  NGetInviteData,
  NRegisterWithInvite,
  NInitiatePasswordRecovery,
  NFinalizePasswordRecovery,
  NGetTimeZones,
  NGetBasicInfo,
  NUpdateBasicInfo,
  NUploadImage,
  NGetInviteRegistrationInfo,
  NUpdatePassword,
  NSendTokenToChangePhone,
  NChangeAccountEmail,
  NUpdateEmailUser,
  NEnableUser,
  NRegister,
} from './types'
import type { TResponseBase } from '~/services/MSAPI/types'

export default class UserControllerUser extends UserControllerBase {
  static readonly controllerPath = 'user'
  private readonly serviceUrl: string

  constructor() {
    super(UserControllerUser.controllerPath)
    this.serviceUrl = this.getFullServiceUrl()
  }

  async getSession(
    organizationId?: string,
    params?: NGetSession.IParams,
  ): TResponseBase<NGetSession.IResponseData> {
    // try {
    return await useFetchInstance(
      `${this.serviceUrl}/getSession/${organizationId || ''}`,
      {
        method: 'GET',
        params,
      },
    )
    // } catch (error: any) {
    //   // const authStore = useAuthStore()
    //   // const toastStore = useToastStore()
    //   // const { $i18n } = useNuxtApp()

    //   // if (error?.response?.status === 401 || error?.status === 401) {
    //   //   toastStore.createToast({
    //   //     type: EToastType.WARNING,
    //   //     content: $i18n.t('auth.session-expired'),
    //   //   })
    //   //   authStore.logout()
    //   // }

    //   throw error
    // }
  }

  async getOrganizationsByUserId(
    params?: NGetOrganizationsByUserId.IParams,
  ): TResponseBase<NGetOrganizationsByUserId.IResponseData> {
    return await useFetchInstance(`${this.serviceUrl}/organizations`, {
      method: 'GET',
      params,
    })
  }

  async getInviteData(
    inviteId: string,
    params?: NGetInviteData.IParams,
  ): TResponseBase<NGetInviteData.IResponseData> {
    return await useFetchInstance(
      `${this.serviceUrl}/inviteregistrationinformation/${inviteId}`,
      {
        method: 'GET',
        params,
      },
    )
  }

  async registerWithInvite(
    inviteId: string,
    body: NRegisterWithInvite.IBody,
  ): TResponseBase<NRegisterWithInvite.IResponseData> {
    return await useFetchInstance(`${this.serviceUrl}/withInvite/${inviteId}`, {
      method: 'POST',
      body,
    })
  }

  async getUserImageSmallURL(userId: string | undefined = undefined) {
    let src = `${this.serviceUrl}/profile/image/small?v=0`

    if (userId) src += '&userId=' + userId

    try {
      const response = await fetch(src, {
        headers: {
          Authorization: `Bearer ${useAuthStore().accessToken}`,
          responseType: 'blob',
        },
      })

      if (!response.ok) {
        throw new Error('Image not found')
      }

      const blob = await response.blob()

      if (blob.size === 0) {
        throw new Error('Invalid image')
      }

      return URL.createObjectURL(blob)
    } catch {
      throw new Error('Image not found')
    }
  }

  async initiatePasswordRecovery(query: NInitiatePasswordRecovery.IQuery) {
    return await useFetchInstance(
      `${this.serviceUrl}/initiate-password-recovery`,
      {
        method: 'POST',
        query: {
          email: query.email,
          recaptchaToken: query.recaptchaToken,
        },
      },
    )
  }

  async finalizePasswordRecovery(body: NFinalizePasswordRecovery.IBody) {
    return await useFetchInstance(
      `${this.serviceUrl}/finalize-password-recovery`,
      {
        method: 'POST',
        body,
      },
    )
  }

  async getTimeZones(): TResponseBase<NGetTimeZones.IResponseData[]> {
    return await useFetchInstance(`${this.serviceUrl}/timezones`, {
      method: 'GET',
    })
  }

  async getBasicInfo(): TResponseBase<NGetBasicInfo.IResponseData> {
    return await useFetchInstance(`${this.serviceUrl}/basic-info`, {
      method: 'GET',
    })
  }

  async getBasicInfoToken(
    userId: string,
    verificationToken: string,
  ): TResponseBase<NGetBasicInfo.IResponseData> {
    return await useFetchInstance(
      `${this.serviceUrl}/${userId}/basic-info-token`,
      {
        method: 'GET',
        query: {
          verificationToken,
        },
      },
    )
  }

  async updateBasicInfo(
    body: NUpdateBasicInfo.IBody,
  ): TResponseBase<NUpdateBasicInfo.TResponseData> {
    return await useFetchInstance(`${this.serviceUrl}/basic-info`, {
      method: 'PUT',
      body,
    })
  }

  async uploadImage(body: NUploadImage.TBody) {
    return await useFetchInstance(`${this.serviceUrl}/upload-image`, {
      method: 'POST',
      body,
    })
  }

  async getInviteRegistrationInfo(
    inviteId: string,
  ): TResponseBase<NGetInviteRegistrationInfo.IResponseData> {
    return await useFetchInstance(
      `${this.serviceUrl}/inviteregistrationinformation/${inviteId}`,
      {
        method: 'GET',
      },
    )
  }

  async updatePassword(
    formData: NUpdatePassword.IRequestData,
  ): TResponseBase<boolean> {
    return await useFetchInstance(`${this.serviceUrl}/change-password`, {
      method: 'POST',
      body: formData,
    })
  }

  async sendTokenToChangePhone(
    formData: NSendTokenToChangePhone.IRequestTokenConfirmation,
  ): TResponseBase<boolean> {
    return await useFetchInstance(`${this.serviceUrl}/phone`, {
      method: 'PATCH',
      body: formData,
    })
  }

  async updateUserPhone(
    formData: NSendTokenToChangePhone.IRequestConfirmationPhoneChange,
  ): TResponseBase<boolean> {
    return await useFetchInstance(`${this.serviceUrl}/phone/confirm`, {
      method: 'POST',
      body: formData,
    })
  }

  async changeAccountEmail(
    body: NChangeAccountEmail.IRequestBody,
  ): TResponseBase<boolean> {
    return await useFetchInstance(`${this.serviceUrl}/email`, {
      method: 'PATCH',
      body,
    })
  }

  async updateUserEmail(
    body: NUpdateEmailUser.IRequestBody,
  ): TResponseBase<boolean> {
    return await useFetchInstance(`${this.serviceUrl}/email/confirm`, {
      method: 'POST',
      body,
    })
  }

  async enableUser(
    userId: string,
    verificationToken: string,
  ): TResponseBase<NEnableUser.TResponseData> {
    return await useFetchInstance(`${this.serviceUrl}/${userId}/enable`, {
      method: 'PATCH',
      body: { verificationToken },
    })
  }

  async disableUser(userId: string): TResponseBase<boolean> {
    return await useFetchInstance(`${this.serviceUrl}/${userId}/disable`, {
      method: 'PATCH',
    })
  }

  async register(
    body: NRegister.IBody,
  ): TResponseBase<NRegister.IResponseData> {
    return await useFetchInstance(`${this.serviceUrl}`, {
      method: 'POST',
      body,
    })
  }

  async canCreateOrganization(): TResponseBase<boolean> {
    return await useFetchInstance(`${this.serviceUrl}/canCreateOrganization`, {
      method: 'GET',
    })
  }
}
